import { useMutation, useQuery } from "@tanstack/react-query";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { ReviewForm } from "./review/ReviewForm";
import { reviewPayload, type ReviewFormValues } from "./review/reviewForm";

// /bookings/review/<id>: the customer reviews what they booked. Port of
// v1's (school)/(chrome)/bookings/review/[bookingId] page + ReviewPageShell
// (the live one; `[school]/bookings/[id]/review` only redirects). The
// booking id is what lets them in, as on v1: GET
// /widget/pages/bookings/:id/review, POST /widget/bookings/:id/review.

type ReviewPage = {
  booking: { _id: string; buyerName?: string; review?: unknown; school?: string | { _id: string } } | null;
  course: { title?: string; schoolId?: string } | null;
};

export function ReviewView({ ctx, bookingId }: { ctx: ViewContext; bookingId: string }) {
  const page = useQuery({
    queryKey: ["booking", "review", ctx.api, bookingId],
    queryFn: () => bookingJson<ReviewPage>(`${ctx.api}/widget/pages/bookings/${encodeURIComponent(bookingId)}/review`),
    retry: (count, error) => count < 2 && !(error instanceof BookingApiError),
  });
  const submit = useMutation({
    mutationFn: (values: ReviewFormValues) =>
      sendJson(`${ctx.api}/widget/bookings/${encodeURIComponent(bookingId)}/review`, "POST", reviewPayload(values)),
  });

  if (page.isPending) return <BookingLoading rows={2} />;

  const notFound = page.isError && page.error instanceof BookingApiError && page.error.status === 404;
  if (notFound || (page.isSuccess && !page.data?.booking)) {
    return (
      <BookingProblem
        title="This booking doesn't seem to belong to you"
        message="Did you really book this event? If so, please contact us."
        action={
          <a className="text-sm font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.contact()}>
            Contact us
          </a>
        }
      />
    );
  }
  if (page.isSuccess && isOtherSchool(ctx.businessId, page.data.booking?.school, page.data.course?.schoolId)) {
    return <RecordNotFound />;
  }
  if (page.isError) {
    return <BookingProblem title="This review page couldn’t be loaded" message="Please try again in a moment." onRetry={() => void page.refetch()} />;
  }

  const { booking, course } = page.data;
  if (submit.isSuccess || booking?.review) {
    return (
      <div className="mx-auto max-w-lg space-y-3 py-8 text-center">
        <h1 className="text-2xl font-semibold">Thank you for your review!</h1>
        <p className="text-muted-foreground">Your review will help others find the best events.</p>
        <a className="font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.events()}>
          Show me more events
        </a>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[500px] space-y-6 py-4">
      <h1 className="text-center text-2xl font-semibold">Please leave us a review for {course?.title ?? "your event"}</h1>
      {submit.isError && <Notice tone="error">{messageOf(submit.error, "Failed to submit the review. Please try again.")}</Notice>}
      <ReviewForm defaultName={booking?.buyerName ?? ""} submitting={submit.isPending} onSubmit={(v) => submit.mutate(v)} />
    </div>
  );
}
